import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, Target, Heart, ArrowRight } from 'lucide-react';
import { useSiteContent } from '../../context/SiteContentContext';

const VisionMissionValues = () => {
  const { siteContent } = useSiteContent();
  const vmv = siteContent?.visionMissionValues || {};

  if (vmv.visible === false) return null;

  const items = [
    {
      key: 'vision',
      icon: Eye,
      label: vmv.vision?.label || 'Our Vision',
      text: vmv.vision?.text || 'A learning environment where every student gains the confidence and skills to communicate clearly in English.',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
    {
      key: 'mission',
      icon: Target,
      label: vmv.mission?.label || 'Our Mission',
      text: vmv.mission?.text || 'We provide personalized, high-quality English instruction that helps learners grow steadily.',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    },
    {
      key: 'values',
      icon: Heart,
      label: vmv.values?.label || 'Core Values',
      text: vmv.values?.text || 'We teach with passion, integrity, and respect for each learner\'s individuality.',
      iconBg: 'bg-slate-100',
      iconColor: 'text-slate-600'
    }
  ];

  return (
    <section className="py-20 sm:py-24 md:py-32 bg-slate-50/50">
      <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="text-xs font-medium text-slate-400 uppercase tracking-[0.2em] mb-4 block">
            {vmv.sectionLabel || 'Our Foundation'}
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            {vmv.sectionTitle || 'Vision, Mission & Values'}
          </h2>
        </motion.div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {items.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-7 sm:p-8 border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all duration-300 h-full flex flex-col">
                  {/* Icon */}
                  <div className={`w-11 h-11 ${item.iconBg} rounded-xl flex items-center justify-center mb-6`}>
                    <Icon className={`w-5 h-5 ${item.iconColor}`} strokeWidth={1.5} />
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-base font-semibold text-slate-900 mb-3 tracking-tight">
                    {item.label}
                  </h3>
                  
                  {/* Text - Short teaser */}
                  <p className="text-slate-500 leading-relaxed text-sm flex-grow">
                    {item.text}
                  </p>
                  
                  {/* Link */}
                  <Link
                    to="/about"
                    className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors mt-6 group/link"
                  >
                    {vmv.readMoreText || 'Read More'}
                    <ArrowRight className="ml-1.5 w-3.5 h-3.5 group-hover/link:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default VisionMissionValues;
